import { useEffect, useState } from "react";
import { ArrowDown, ArrowUpRight, Play } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { EpisodeCard } from "@/components/EpisodeCard";
import { Modal } from "@/components/Modal";
import { VideoEmbed } from "@/components/VideoEmbed";
import { Reveal } from "@/components/Reveal";
import { ThemePlayer } from "@/components/ThemePlayer";

const KINDS = ["All", "Full Episode", "Interview Clip", "Cooking Segment", "Guest Moment", "Behind the Scenes", "Media & Press"];

export default function Watch() {
  const [platforms, setPlatforms] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [filter, setFilter] = useState("All");
  const [playing, setPlaying] = useState(null);
  const [seasonPreview, setSeasonPreview] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/content/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
    api.get("/content/episodes").then((r) => setEpisodes(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "All" ? episodes : episodes.filter((e) => e.kind === filter);
  const featured = episodes.find((e) => e.thumbnail);
  const visibleEpisodes = showAll ? filtered : filtered.slice(0, 6);
  const openVideo = (ep) => {
    if (ep.kind === "Media & Press") {
      window.open(ep.video_url, "_blank", "noopener");
      return;
    }
    window.dispatchEvent(new Event("poolside:video-play"));
    setPlaying(ep);
  };
  const openPreview = () => {
    window.dispatchEvent(new Event("poolside:video-play"));
    setSeasonPreview(true);
  };

  return (
    <div data-testid="watch-page">
      {/* HERO — bright water brand moment with host imagery */}
      <section className="relative overflow-hidden" data-testid="watch-hero">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-white/25 pointer-events-none" aria-hidden="true" />
        <div className="relative container-site grid lg:grid-cols-2 gap-12 lg:gap-10 items-center py-14 sm:py-16 lg:py-20">
          <Reveal className="relative z-10">
            <p className="label-eyebrow mb-4" data-testid="watch-eyebrow">Watch Poolside</p>
            <h1 className="font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-ink" data-testid="watch-hero-title">
              Watch Poolside <span className="text-pool">Cooking.</span>
            </h1>
            <p className="mt-5 text-ink/70 text-sm sm:text-base leading-relaxed max-w-md" data-testid="watch-hero-sub">
              Your seat is always waiting. Watch Poolside Cooking on television, streaming and wherever you like to tune in.
            </p>
            <a
              href="#where-to-watch"
              data-testid="watch-hero-cta"
              className="mt-8 inline-flex items-center justify-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full shadow-[0_14px_40px_rgba(2,150,180,0.25)] transition-all hover:-translate-y-0.5"
            >
              See Where to Watch <ArrowDown className="h-4 w-4" />
            </a>
          </Reveal>
          <Reveal delay={0.12} className="relative">
            <div className="relative max-w-lg ml-auto w-full">
              <div className="bg-white p-2.5 rounded-[1.6rem] shadow-[0_30px_80px_rgba(2,60,80,0.18)]" data-testid="watch-hero-image">
                <div className="overflow-hidden rounded-[1.25rem]">
                  <img
                    src="/assets/ashley-host-chair.jpg"
                    alt="Ashley Marie hosting Poolside Cooking"
                    className="w-full h-[320px] sm:h-[400px] lg:h-[430px] object-cover object-top"
                  />
                </div>
              </div>
              <div
                className="absolute -bottom-5 left-4 bg-white rounded-2xl px-5 py-4 shadow-[0_16px_44px_rgba(2,60,80,0.15)] border border-slate-100"
                data-testid="watch-hero-tagline-card"
              >
                <p className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase font-bold text-slate-400 mb-1.5">
                  Ashley&rsquo;s Signature Question
                </p>
                <p className="font-script text-2xl sm:text-3xl text-pool leading-none">Chef, Where You At?</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PLATFORMS */}
      <section id="where-to-watch" className="relative py-14 sm:py-20 bg-white overflow-hidden" data-testid="watch-platforms">
        <span className="pointer-events-none select-none absolute -top-10 right-0 font-script text-[16vw] leading-none text-pool/5" aria-hidden="true">Watch</span>
        <div className="absolute bottom-0 -left-20 h-64 w-64 rounded-full bg-pool-light/40 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative container-site">
          <Reveal className="mb-12 max-w-2xl">
            <p className="label-eyebrow mb-4">Watch Anywhere</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink" data-testid="watch-anywhere-title">
              Poolside Is Always <span className="text-pool">Within Reach.</span>
            </h2>
            <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
              TV. Streaming. YouTube. However you watch, there&rsquo;s a seat waiting for you Poolside.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
            {platforms.map((p, i) => {
              const isBroadcast = p.name.toLowerCase().includes("wgnt");
              return (
                <Reveal key={p.id} delay={i * 0.06} className="h-full">
                  <div
                    className="group bg-white/75 backdrop-blur-md border border-pool/20 rounded-2xl p-6 sm:p-7 h-full flex flex-col items-center text-center shadow-[0_10px_34px_rgba(2,60,80,0.08)] transition-all duration-300 hover:-translate-y-1.5 hover:border-pool hover:shadow-[0_22px_54px_rgba(2,150,180,0.22)]"
                    data-testid={`watch-platform-${p.id}`}
                  >
                    <div className="h-14 sm:h-16 flex items-center justify-center">
                      {p.logo ? (
                        <img
                          src={imgSrc(p.logo)}
                          alt={p.name}
                          className="max-h-14 sm:max-h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <p className="font-display font-black text-lg text-ink">{p.name}</p>
                      )}
                    </div>
                    <p className="mt-4 font-display font-bold text-sm text-ink">{p.name}</p>
                    {isBroadcast && p.detail ? (
                      <span
                        className="mt-2.5 inline-block bg-pool text-white text-[10px] sm:text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-1.5 rounded-full shadow-[0_8px_22px_rgba(2,150,180,0.35)]"
                        data-testid="watch-wgnt-badge"
                      >
                        Sundays &bull; 8:30 AM
                      </span>
                    ) : (
                      p.detail && <p className="mt-2 text-xs text-slate-500">{p.detail}</p>
                    )}
                    <div className="flex-1" />
                    {p.link && (
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 bg-pool hover:bg-pool-dark text-white text-[10px] sm:text-[11px] tracking-[0.15em] uppercase font-bold px-5 py-2.5 rounded-full transition-all shadow-[0_8px_22px_rgba(2,150,180,0.28)] hover:-translate-y-0.5"
                        data-testid={`watch-platform-link-${p.id}`}
                      >
                        Watch Episodes <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE SOUND OF POOLSIDE — theme song */}
      <section className="relative py-8 sm:py-10 bg-gradient-to-b from-white via-pool-light/25 to-cream overflow-hidden" data-testid="theme-section">
        <div className="absolute -top-16 left-[8%] h-64 w-64 rounded-full bg-pool/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-0 right-[6%] h-48 w-48 rounded-[3rem] bg-amber-300/15 blur-2xl rotate-12 pointer-events-none" aria-hidden="true" />
        <div className="absolute top-1/3 -right-20 h-72 w-72 rounded-full bg-pool-light/40 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative container-site max-w-4xl">
          <Reveal className="text-center mb-5">
            <p className="label-eyebrow mb-2.5 justify-center">The Sound of Poolside</p>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-ink" data-testid="theme-headline">
              Turn It <span className="text-pool">Up.</span>
            </h2>
            <p className="mt-2 text-slate-600 text-sm">Press play on the official Poolside Cooking theme.</p>
          </Reveal>
          <Reveal delay={0.08}>
            <ThemePlayer />
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-5">
              <p className="font-script text-lg sm:text-xl text-pool" data-testid="theme-tagline">Sounds like Poolside. ☀️</p>
              <a
                href="https://music.apple.com/us/album/poolside-cooking-official-theme-song/1544779085?i=1544779271"
                target="_blank"
                rel="noreferrer"
                className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 hover:text-pool transition-colors"
                data-testid="theme-apple-music"
              >
                Stream the full song on Apple Music ↗
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FUN POOLSIDE DETAIL — micro brand moment */}
      <div className="relative bg-white overflow-hidden" data-testid="watch-script-moment">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <div className="ripple-pass w-3/4 max-w-2xl h-12 bg-gradient-to-r from-transparent via-pool/10 to-transparent rounded-full blur-md" />
        </div>
        <Reveal className="relative py-8 sm:py-10 text-center">
          <p className="font-script text-3xl sm:text-4xl lg:text-5xl text-pool -rotate-2 inline-block" data-testid="watch-script-phrase">
            Grab a seat. We&rsquo;re going Poolside.
          </p>
        </Reveal>
      </div>

      {/* FEATURED POOLSIDE */}
      {featured && (
        <section className="relative py-14 sm:py-20 bg-cream border-t border-pool/10 overflow-hidden" data-testid="featured-poolside">
          <div className="absolute -top-16 right-[8%] h-64 w-64 rounded-full bg-pool/10 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="absolute bottom-0 left-[4%] h-40 w-40 rounded-[3rem] bg-amber-300/20 blur-2xl rotate-12 pointer-events-none" aria-hidden="true" />
          <div className="relative container-site">
            <Reveal className="mb-10">
              <p className="label-eyebrow mb-4">Featured Poolside</p>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink" data-testid="featured-poolside-title">
                Pull Up a <span className="text-pool">Chair.</span>
              </h2>
            </Reveal>
            <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-10 items-center">
              <Reveal className="relative">
                <button type="button" onClick={() => openVideo(featured)} className="group block w-full text-left" data-testid="featured-poolside-thumb">
                  <div className="relative overflow-hidden rounded-[1.75rem] shadow-[0_26px_70px_rgba(2,60,80,0.18)]">
                    <img
                      src={imgSrc(featured.thumbnail)}
                      alt={featured.title}
                      loading="lazy"
                      className="img-zoom w-full aspect-video object-cover"
                    />
                    <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/15 transition-colors duration-300" aria-hidden="true" />
                    <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                      <span className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/85 backdrop-blur-md border border-white/90 shadow-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-7 w-7 text-pool ml-1" />
                      </span>
                    </span>
                  </div>
                </button>
              </Reveal>
              <Reveal delay={0.1}>
                <div
                  className="bg-white/75 backdrop-blur-xl border border-pool/20 rounded-3xl p-7 sm:p-9 shadow-[0_18px_50px_rgba(2,60,80,0.12)] lg:-ml-16 relative z-10"
                  data-testid="featured-poolside-card"
                >
                  <p className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-pool font-bold mb-3">{featured.kind}</p>
                  <h3 className="font-display font-extrabold text-xl sm:text-2xl text-ink leading-snug" data-testid="featured-poolside-episode-title">
                    {featured.title}
                  </h3>
                  {featured.description && (
                    <p className="mt-3 text-slate-600 text-sm leading-relaxed">{featured.description}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => openVideo(featured)}
                    className="mt-6 inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-3.5 rounded-full shadow-[0_10px_28px_rgba(2,150,180,0.3)] transition-all hover:-translate-y-0.5"
                    data-testid="featured-poolside-watch"
                  >
                    Watch Episode <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* NOW STREAMING — seasons */}
      <section className="py-14 sm:py-16 bg-white border-t border-slate-100" data-testid="seasons-section">
        <div className="container-site">
          <Reveal className="text-center mb-12">
            <p className="label-eyebrow mb-4 justify-center">Now Streaming</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-ink">
              Every Season. <span className="text-pool">Every Vibe.</span>
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
              Seasons 1 &amp; 2 of Poolside Cooking are streaming now on Your Channel Network.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6 max-w-xl mx-auto" data-testid="seasons-row">
            <Reveal>
              <button type="button" onClick={openPreview} className="group block w-full text-left" data-testid="season-poster-1">
                <div className="relative aspect-[2/3] overflow-hidden rounded-2xl shadow-[0_24px_60px_rgba(2,60,80,0.2)]">
                  <img src="/assets/season-1.jpg" alt="Poolside Cooking Season 1" loading="lazy" className="img-zoom h-full w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent p-5 pt-14 flex items-center gap-2 text-white text-[11px] tracking-[0.2em] uppercase font-bold">
                    <Play className="h-4 w-4 text-pool-light" /> Explore Season 1
                  </span>
                </div>
              </button>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="group block" data-testid="season-poster-2">
                <div className="aspect-[2/3] overflow-hidden rounded-2xl shadow-[0_24px_60px_rgba(2,60,80,0.2)]">
                  <img src="/assets/season-2.jpg" alt="Poolside Cooking Season 2" loading="lazy" className="img-zoom h-full w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EPISODES & CLIPS */}
      <section className="relative py-14 sm:py-16 bg-white border-t border-slate-100 overflow-hidden" data-testid="watch-episodes">
        <div className="absolute top-10 -right-20 h-60 w-60 rounded-full bg-pool-light/30 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative container-site">
          <Reveal className="mb-10">
            <p className="label-eyebrow mb-4">Watch More Poolside</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink" data-testid="watch-library-heading">
              From the <span className="text-pool">Show.</span>
            </h2>
          </Reveal>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="watch-filters">
            {KINDS.map((k) => (
              <button
                key={k}
                onClick={() => { setFilter(k); setShowAll(false); }}
                data-testid={`filter-${k.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] tracking-[0.15em] uppercase font-bold px-5 py-2.5 rounded-full border transition-colors ${
                  filter === k
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                }`}
              >
                {k}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="watch-grid">
            {visibleEpisodes.map((ep, i) => (
              <Reveal key={ep.id} delay={(i % 3) * 0.08}>
                <EpisodeCard episode={ep} onPlay={openVideo} />
              </Reveal>
            ))}
          </div>

          {!showAll && filtered.length > 6 && (
            <Reveal className="mt-10 text-center">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                data-testid="watch-view-more"
                className="inline-flex items-center gap-2.5 border border-pool/30 text-pool-deep hover:border-pool hover:text-pool font-bold text-xs tracking-[0.18em] uppercase px-7 py-3.5 rounded-full transition-colors"
              >
                View More <ArrowDown className="h-4 w-4" />
              </button>
            </Reveal>
          )}

          {filtered.length === 0 && (
            <p className="text-slate-500 text-sm py-16 text-center" data-testid="watch-empty">
              Nothing here yet — new {filter.toLowerCase()} content is coming soon.
            </p>
          )}
        </div>
      </section>

      <Modal open={!!playing} onClose={() => setPlaying(null)} testId="watch-video-modal">
        {playing && (
          <div>
            <VideoEmbed url={playing.video_url} title={playing.title} />
            <div className="bg-ink p-6 sm:p-8 text-white">
              <p className="text-[10px] tracking-[0.25em] uppercase text-pool font-bold mb-2">{playing.kind}</p>
              <h3 className="font-display font-extrabold text-2xl">{playing.title}</h3>
              {playing.description && <p className="mt-3 text-white/70 text-sm leading-relaxed">{playing.description}</p>}
            </div>
          </div>
        )}
      </Modal>
      <Modal open={seasonPreview} onClose={() => setSeasonPreview(false)} testId="season-preview-modal">
        <div className="bg-ink rounded-2xl overflow-hidden shadow-2xl">
          <video src="/assets/season-1-preview.mp4" controls autoPlay playsInline className="w-full aspect-video bg-black" data-testid="season-preview-player" />
          <div className="p-6 sm:p-8 text-white">
            <p className="text-[10px] tracking-[0.25em] uppercase text-pool font-bold mb-2">Season 1 Preview</p>
            <h3 className="font-display font-extrabold text-2xl">Poolside Cooking</h3>
          </div>
        </div>
      </Modal>
    </div>
  );
}
